import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "shared/auth/hooks/useCurrentUser";
import {
  logoutUser,
  updateUserProfile,
} from "shared/api/client/orvalHelpers";
import ChangePasswordModal from "../components/modals/ChangePasswordModal";
import ChangeEmailModal from "../components/modals/ChangeEmailModal";
import DeleteAccountModal from "../components/modals/DeleteAccountModal";
import { OrderHistoryPage } from "features/orders";
import EmailVerificationBanner from "../components/EmailVerificationBanner";
import OrganizationTeamPanel from "../components/OrganizationTeamPanel";
import { getCookie, getAuthCookieAttributes } from "shared/auth/utils/cookieUtils";
import { parseApiError } from "shared/api/client/errorParser";
import { normalizeUserRole } from "shared/auth/constants/roles";
import "styles/legacy/Profile.css";

type AccountTabKey = "account-details" | "organization" | "order-history" | "payment-methods";

const ACCOUNT_TABS: Array<{ key: AccountTabKey; label: string }> = [
  { key: "account-details", label: "Account details" },
  { key: "organization", label: "Organization & team" },
  { key: "order-history", label: "Order history" },
];

const ProfilePage: React.FC = () => {
  const user = useCurrentUser();
  const token = getCookie("userToken");
  const [isVerified, setIsVerified] = useState(
    getCookie("userIsVerified") === "true"
  );
  const role = normalizeUserRole(getCookie("userRole"));
  const isCreator = role === "CREATOR";

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const cookieFirstName = getCookie("userFirstName") || "";
  const cookieLastName = getCookie("userLastName") || "";
  const cookieEmail = getCookie("userEmail") || "";

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [changedEmail, setChangedEmail] = useState<string | null>(null);

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || cookieFirstName);
      setLastName(user.lastName || cookieLastName);
      setEmail(changedEmail ?? user.email ?? cookieEmail);
    } else {
      setFirstName(cookieFirstName);
      setLastName(cookieLastName);
      setEmail(cookieEmail);
    }
  }, [user, cookieFirstName, cookieLastName, cookieEmail, changedEmail]);

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [activeTab, setActiveTab] = useState<AccountTabKey>("account-details");

  const handlePasswordReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowChangePasswordModal(true);
  };

  const handleDeleteAccount = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowDeleteAccountModal(true);
  };

  const handleEditButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowChangeEmailModal(true);
  };

  const handleCancelChanges = () => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setEmail(initialEmail);
    setFirstNameError(false);
    setLastNameError(false);
  };

  const navigate = useNavigate();
  const handleLogout = () => {
    if (!token) {
      navigate("/login");
      return;
    }

    logoutUser()
      .catch(() => {})
      .finally(() => {
        document.cookie =
          "userToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userEmail=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userFirstName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userLastName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userIsVerified=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "userRole=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        navigate("/login");
      });
  };

  const handleSaveChanges = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let hasError = false;

    if (!firstName.trim()) {
      setFirstNameError(true);
      hasError = true;
    } else {
      setFirstNameError(false);
    }

    if (!lastName.trim()) {
      setLastNameError(true);
      hasError = true;
    } else {
      setLastNameError(false);
    }

    if (hasError) return;

    if (!user || !user.id) {
      alert("User not loaded.");
      return;
    }

    try {
      setErrorMessage("");
      await updateUserProfile(user.id, {
        email,
        firstName,
        lastName,
      });

      setSuccessMessage("Profile updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      setInitialFirstName(firstName);
      setInitialLastName(lastName);
      setInitialEmail(email);
    } catch (error) {
      setErrorMessage(parseApiError(error, "Failed to update profile."));
      console.error(error);
    }
  };

  const [initialFirstName, setInitialFirstName] = useState("");
  const [initialLastName, setInitialLastName] = useState("");
  const [initialEmail, setInitialEmail] = useState("");

  useEffect(() => {
    const finalFirstName = user?.firstName || cookieFirstName;
    const finalLastName = user?.lastName || cookieLastName;
    const finalEmail = changedEmail ?? user?.email ?? cookieEmail;

    setFirstName(finalFirstName);
    setLastName(finalLastName);
    setEmail(finalEmail);
    setInitialFirstName(finalFirstName);
    setInitialLastName(finalLastName);
    setInitialEmail(finalEmail);
  }, [user, cookieFirstName, cookieLastName, cookieEmail, changedEmail]);

  const hasChanges =
    firstName !== initialFirstName ||
    lastName !== initialLastName ||
    email !== initialEmail;
  const isAccountDetailsTab = activeTab === "account-details";

  const renderAccountDetailsPage = () => (
    <div className="information-container-profile account-details-panel-profile">
      <form onSubmit={handleSaveChanges}>
        <div className="form-fields-profile account-form-fields-profile">
          <div className="name-row-profile">
            <div
              className="form-group-profile account-input-group-profile account-input-group-profile--first"
              id={firstNameError ? "incorrect-input-profile" : undefined}
            >
              <label htmlFor="firstName" className="form-label-profile">
                First name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-field-profile account-field-profile"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div
              className="form-group-profile account-input-group-profile"
              id={lastNameError ? "incorrect-input-profile" : undefined}
            >
              <label htmlFor="lastName" className="form-label-profile">
                Last name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-field-profile account-field-profile"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group-profile account-input-group-profile account-email-group-profile">
            <label htmlFor="email" className="form-label-profile">
              Email
            </label>
            <div className="email-profile-container account-email-container-profile">
              <div className="linked-account-container-profile account-email-chip-profile">
                <span className="linked-email-profile">{email}</span>
              </div>
              <button className="edit-button-profile" onClick={handleEditButton}>
                <img
                  src="/images/icons/edit.svg"
                  className="edit-icon-profile"
                  alt="Icon"
                />
              </button>
            </div>
          </div>

          <div className="form-group-profile account-input-group-profile" id="form-button-profile">
            <button
              className="password-button-profile account-password-button-profile"
              onClick={handlePasswordReset}
            >
              Change Password
            </button>
          </div>
        </div>

        <div className="bottom-buttons-profile account-action-buttons-profile">
          <button
            type="submit"
            className="form-button-profile account-save-button-profile"
            disabled={!hasChanges}
          >
            Save
          </button>
          <button
            type="button"
            className="account-cancel-button-profile"
            onClick={handleCancelChanges}
          >
            Cancel
          </button>
        </div>

        <div className="account-divider-profile" />

        <div className="danger-zone-profile">
          <div className="danger-zone-title-profile">Danger Zone</div>
          <button
            type="button"
            className="delete-account-button-profile danger-delete-button-profile"
            onClick={handleDeleteAccount}
          >
            Delete Account
          </button>
        </div>
      </form>
    </div>
  );

  const renderTabPage = () => {
    if (activeTab === "organization") {
      return (
        <OrganizationTeamPanel
          currentUserId={user?.id ?? getCookie("userId")}
          currentUserEmail={email || cookieEmail}
        />
      );
    }

    if (activeTab === "order-history") {
      return <OrderHistoryPage />;
    }

    if (activeTab === "payment-methods") {
      return (
        <div className="payment-methods-container-profile">
          <h1 className="payment-methods-title-profile">Payment</h1>
        </div>
      );
    }

    return renderAccountDetailsPage();
  };

  return (
    <div className="content-profile">
      {successMessage && (
        <div className="password-success-box">{successMessage}</div>
      )}
      {errorMessage && <div className="password-error-box" role="alert">{errorMessage}</div>}
      <div className="main-profile">
        <div className="account-tabs-profile" role="tablist" aria-label="Account tabs">
          {ACCOUNT_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={
                activeTab === tab.key
                  ? "account-tab-trigger-profile account-tab-trigger-profile--active"
                  : "account-tab-trigger-profile"
              }
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === "organization" && (
          <div className="top-container-profile">
            <div className="left-top-container-profile">
              <div className="title-top-container-profile">Organization &amp; team</div>
              <div className="subtitle-top-container-profile">
                {isCreator
                  ? "Manage your studio profile and invite collaborators"
                  : "Manage the organizations you belong to"}
              </div>
            </div>
          </div>
        )}
        {isAccountDetailsTab && (
          <>
            <div className="top-container-profile">
              <div className="left-top-container-profile">
                <div className="title-top-container-profile">Account</div>
                <div className="subtitle-top-container-profile">
                  Edit your profile details
                </div>
              </div>
              <div className="right-top-container-profile">
                <button className="logout-button-profile" onClick={handleLogout}>
                  <img
                    src="/images/icons/logout.svg"
                    className="logout-icon-profile"
                    alt="Icon"
                  />
                  <div className="logout-text-profile">Logout</div>
                </button>
              </div>
            </div>
            {!isVerified && (
              <EmailVerificationBanner
                className="alert-div-profile"
                onVerified={() => setIsVerified(true)}
              />
            )}
          </>
        )}
        <ChangePasswordModal
          open={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          setSuccessMessage={setSuccessMessage}
        />
        <ChangeEmailModal
          onEmailChanged={(value) => {
            setChangedEmail(value);
            setEmail(value);
            setInitialEmail(value);
            document.cookie = `userEmail=${encodeURIComponent(value)}; ${getAuthCookieAttributes()}`;
          }}
          open={showChangeEmailModal}
          onClose={() => setShowChangeEmailModal(false)}
          setSuccessMessage={setSuccessMessage}
        />
        <DeleteAccountModal
          open={showDeleteAccountModal}
          onClose={() => setShowDeleteAccountModal(false)}
          userId={user?.id ?? ""}
        />
        {renderTabPage()}
      </div>
    </div>
  );
};

export default ProfilePage;
